import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Star, Trash2, ChevronLeft, ChevronRight, AlertTriangle, Plus } from "lucide-react";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import {
  adminGetProduct,
  adminListCategories,
  adminListTypes,
  adminCreateType,
  adminCreateProduct,
  adminUpdateProduct,
  adminUploadImage,
  adminReplaceImage,
  adminDeleteImage,
  adminReorderImages,
} from "../../services/api";
import { getProductImage } from "../../data/images";

const MAX_IMAGES = 4;
const VALID_EXT = ["png", "jpg", "jpeg", "webp"];
const MAX_BYTES = 10 * 1024 * 1024;
const SLOT_LABELS = ["Main photo", "Angle", "Close-up", "On wrist"];

const label = "text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-charcoal";
const input = "mt-2 w-full border border-brand-sand bg-brand-surface px-4 py-3 text-sm text-brand-charcoal outline-none transition-colors focus:border-brand-orange";
const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const empty = {
  name: "", sku: "", category: "", type: "", short_description: "", full_description: "",
  price: "", compare_price: "", cost_price: "", stock: "", low_stock_alert: "5", tags: "", featured: false, published: false,
};

export default function AdminProductForm() {
  const { slug } = useParams();
  const isNew = slug === undefined || slug === "new";
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [pending, setPending] = useState(null);
  const [newType, setNewType] = useState("");
  const [showNewType, setShowNewType] = useState(false);
  const addInputRef = useRef(null);

  const apply = (p) => {
    setProduct(p);
    setForm({
      name: p.name || "", sku: p.sku || "", category: p.category || "", type: p.type || "",
      short_description: p.short_description || "", full_description: p.full_description || "",
      price: p.price != null ? String(p.price) : "", compare_price: p.mrp != null ? String(p.mrp) : "",
      cost_price: p.cost_price != null ? String(p.cost_price) : "", stock: p.stock != null ? String(p.stock) : "",
      low_stock_alert: p.low_stock_alert != null ? String(p.low_stock_alert) : "5",
      tags: (p.tags || []).join(", "), featured: !!(p.flags && p.flags.featured), published: p.published !== false,
    });
  };

  useEffect(() => {
    if (!ready || user?.role !== "admin") { setLoading(false); return; }
    setLoading(true);
    setProduct(null);
    Promise.all([adminListCategories(), adminListTypes()]).then(([cats, ts]) => {
      setCategories(cats); setTypes(ts);
      if (isNew) { setForm((f) => ({ ...f, category: cats[0]?.slug || "" })); setLoading(false); return; }
      adminGetProduct(slug).then(apply).catch((e) => (e?.response?.status === 404 ? setNotFound(true) : toast.error("Could not load bracelet."))).finally(() => setLoading(false));
    }).catch(() => { toast.error("Could not load form data."); setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, user, slug]);

  const detail = (err, fb) => err?.response?.data?.detail || fb;
  const num = (v) => (v === "" ? undefined : Number(v));
  const tagsArr = (s) => s.split(",").map((t) => t.trim()).filter(Boolean);
  const priceValid = form.price === "" || (Number.isInteger(Number(form.price)) && Number(form.price) >= 0);

  const addType = async () => {
    if (!newType.trim()) return;
    try {
      const t = await adminCreateType(newType.trim());
      setTypes((ts) => [...ts, t].sort((a, b) => a.name.localeCompare(b.name)));
      setForm((f) => ({ ...f, type: t.slug })); setNewType(""); setShowNewType(false);
      toast.success(`Type "${t.name}" added.`);
    } catch (err) { toast.error(detail(err, "Could not add type.")); }
  };

  const onCreate = async () => {
    if (!form.name.trim()) return toast.error("Bracelet name is required.");
    if (!form.category) return toast.error("Choose a category.");
    if (!priceValid) return toast.error("Price must be a whole number in INR.");
    setSaving(true);
    try {
      const created = await adminCreateProduct({
        name: form.name, category: form.category, type: form.type || undefined, sku: form.sku || undefined,
        short_description: form.short_description || undefined, full_description: form.full_description || undefined,
        price: num(form.price), compare_price: num(form.compare_price), cost_price: num(form.cost_price),
        stock: num(form.stock), low_stock_alert: num(form.low_stock_alert), tags: tagsArr(form.tags), featured: form.featured,
      });
      toast.success("Bracelet created as draft. Add up to 4 photos, then publish.");
      navigate(`/admin/products/${created.slug}`);
    } catch (err) { toast.error(detail(err, "Create failed.")); }
    finally { setSaving(false); }
  };

  const onSave = async () => {
    if (!priceValid) return toast.error("Price must be a whole number in INR.");
    setSaving(true);
    try {
      const body = {
        name: form.name, category: form.category, type: form.type, sku: form.sku,
        short_description: form.short_description, full_description: form.full_description,
        tags: tagsArr(form.tags), featured: form.featured, published: form.published,
      };
      if (form.price !== "") body.price = Number(form.price);
      if (form.compare_price !== "") body.compare_price = Number(form.compare_price);
      if (form.cost_price !== "") body.cost_price = Number(form.cost_price);
      if (form.stock !== "") body.stock = Number(form.stock);
      if (form.low_stock_alert !== "") body.low_stock_alert = Number(form.low_stock_alert);
      apply(await adminUpdateProduct(slug, body));
      toast.success("Bracelet saved.");
    } catch (err) { toast.error(detail(err, "Save failed.")); setForm((f) => ({ ...f, published: product?.published !== false })); }
    finally { setSaving(false); }
  };

  const pickFile = (file, replaceIndex = null) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!VALID_EXT.includes(ext)) return toast.error("Only PNG, JPG and WebP photos are allowed.");
    if (file.size > MAX_BYTES) return toast.error("Photo exceeds 10 MB.");
    if (replaceIndex === null && (product.images || []).length >= MAX_IMAGES) return toast.error("Maximum 4 photos per bracelet.");
    setPending({ file, preview: URL.createObjectURL(file), replaceIndex });
  };
  const cancelPending = () => { if (pending) URL.revokeObjectURL(pending.preview); setPending(null); if (addInputRef.current) addInputRef.current.value = ""; };
  const savePending = async () => {
    if (!pending) return; setSaving(true);
    try {
      const updated = pending.replaceIndex === null ? await adminUploadImage(slug, pending.file) : await adminReplaceImage(slug, pending.replaceIndex, pending.file);
      apply(updated); toast.success("Photo saved.");
    } catch (err) { toast.error(detail(err, "Upload failed.")); }
    finally { setSaving(false); cancelPending(); }
  };
  const setMain = async (path) => { try { apply(await adminUpdateProduct(slug, { main_image: path })); toast.success("Main photo set."); } catch (err) { toast.error(detail(err, "Failed.")); } };
  const move = async (i, d) => {
    const imgs = [...(product.images || [])]; const t = i + d;
    if (t < 0 || t >= imgs.length) return;
    [imgs[i], imgs[t]] = [imgs[t], imgs[i]];
    try { apply(await adminReorderImages(slug, imgs)); } catch (err) { toast.error(detail(err, "Reorder failed.")); }
  };
  const removeImage = async (i) => { if (!window.confirm("Remove this photo?")) return; try { apply(await adminDeleteImage(slug, i)); toast.success("Photo removed."); } catch (err) { toast.error(detail(err, "Delete failed.")); } };

  if (!ready || loading) return <AdminLayout title="…"><p data-testid="form-loading" className="text-brand-stone">Loading…</p></AdminLayout>;
  if (notFound) return <AdminLayout title="Not found"><p data-testid="form-not-found" className="text-brand-stone">Bracelet not found.</p></AdminLayout>;
  if (!isNew && !product) return <AdminLayout title="…"><p data-testid="form-loading" className="text-brand-stone">Loading…</p></AdminLayout>;

  const images = product?.images || [];
  const needsType = !isNew && !product?.type;
  const missing = [];
  if (!form.type) missing.push("Type");
  if (form.price === "" || Number(form.price) <= 0) missing.push("Price");
  if (form.stock === "") missing.push("Stock");
  if (!isNew && images.length === 0) missing.push("Photo");
  const canPublish = !isNew && (missing.length === 0 || (product?.ever_published && missing.every((m) => m === "Type")));

  return (
    <AdminLayout title={isNew ? "Add bracelet" : product?.name || "Edit bracelet"} subtitle={isNew ? "New draft — add photos after creating, then publish" : `SKU ${product?.sku || "—"} · /product/${product?.slug}`}>
      <div data-testid="admin-product-form">
        <Link to="/admin/products" data-testid="form-back" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-brand-stone transition-colors hover:text-brand-orange">
          <ArrowLeft className="h-3.5 w-3.5" /> Bracelets
        </Link>

        {needsType && (
          <div className="mt-5 flex items-start gap-3 border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800" data-testid="needs-review-banner">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p><strong>Needs review:</strong> this bracelet was imported without a Type. It stays published — choose a Type below and save when you get a chance.</p>
          </div>
        )}

        <div className="mt-6 grid gap-8 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <section className="border border-brand-sand bg-brand-surface p-6">
              <p className="font-serif text-xl text-brand-charcoal">Bracelet details</p>
              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <div>
                  <label className={label} htmlFor="f-cat">1 · Category *</label>
                  <select id="f-cat" data-testid="field-category" className={input} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label} htmlFor="f-type">3 · Type {isNew ? "(required before publish)" : "*"}</label>
                  <select id="f-type" data-testid="field-type" className={`${input} ${!form.type ? "border-amber-400" : ""}`} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                    <option value="">— Not set (needs review) —</option>
                    {types.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
                  </select>
                  {showNewType ? (
                    <div className="mt-2 flex gap-2">
                      <input value={newType} onChange={(e) => setNewType(e.target.value)} placeholder="e.g. Adjustable" data-testid="new-type-input" className="w-full border border-brand-sand bg-white px-3 py-2 text-sm outline-none focus:border-brand-orange" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addType())} />
                      <button type="button" onClick={addType} data-testid="new-type-save" className="bg-brand-charcoal px-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-brand-cream hover:bg-brand-orange">Add</button>
                      <button type="button" onClick={() => setShowNewType(false)} className="text-[10px] font-semibold uppercase text-brand-stone">Cancel</button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => setShowNewType(true)} data-testid="new-type-toggle" className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-brand-orange hover:underline"><Plus className="h-3 w-3" /> New type</button>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <label className={label} htmlFor="f-name">2 · Bracelet name *</label>
                  <input id="f-name" data-testid="field-name" className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Aqua Star Charm Bracelet" />
                  <p className="mt-2 text-xs text-brand-stone" data-testid="slug-preview">
                    URL: /product/<span className="text-brand-charcoal">{isNew ? slugify(form.name) || "…" : product.slug}</span>
                    {isNew && <span className="ml-2 text-brand-stone/70">(auto-generated, made unique on save)</span>}
                  </p>
                </div>
                <div>
                  <label className={label} htmlFor="f-price">5 · Price (₹ INR) *</label>
                  <input id="f-price" data-testid="field-price" type="number" min="0" step="1" inputMode="numeric" className={`${input} ${!priceValid ? "border-red-400" : ""}`} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="499" />
                  {!priceValid && <p className="mt-1 text-xs text-red-600" data-testid="price-error">Whole rupees only, ₹0 or more.</p>}
                </div>
                <div>
                  <label className={label} htmlFor="f-stock">Stock quantity *</label>
                  <input id="f-stock" data-testid="field-stock" type="number" min="0" className={input} value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} />
                </div>
              </div>
            </section>

            {!isNew && (
              <section className="border border-brand-sand bg-brand-surface p-6" data-testid="form-images">
                <div className="flex items-center justify-between">
                  <p className="font-serif text-xl text-brand-charcoal">4 · Photos ({images.length}/{MAX_IMAGES})</p>
                  <p className="text-xs text-brand-stone">PNG / JPG / WebP · max 10 MB each · square, white background</p>
                </div>
                <p className="mt-2 text-xs text-brand-stone">First photo is shown on the shop card. Use the arrows to reorder, the star to set the main photo.</p>
                <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
                  {Array.from({ length: MAX_IMAGES }).map((_, n) => {
                    const path = images[n];
                    if (!path) {
                      return n === images.length ? (
                        <button key={`add-${n}`} onClick={() => addInputRef.current?.click()} data-testid="add-image" className="flex aspect-[4/5] flex-col items-center justify-center gap-2 border border-dashed border-brand-sand text-brand-stone transition-colors hover:border-brand-orange hover:text-brand-orange">
                          <span className="font-serif text-3xl">+</span><span className="text-[10px] font-semibold uppercase tracking-[0.16em]">{SLOT_LABELS[n]}</span>
                        </button>
                      ) : (
                        <div key={`empty-${n}`} className="flex aspect-[4/5] items-center justify-center border border-dashed border-brand-sand/60 text-[10px] font-semibold uppercase tracking-[0.16em] text-brand-stone/50">{SLOT_LABELS[n]}</div>
                      );
                    }
                    return (
                      <div key={path} className="border border-brand-sand" data-testid={`image-${n}`}>
                        <div className="relative aspect-square bg-white">
                          <img src={getProductImage(path, product.name).src} alt={`${product.name} — photo ${n + 1}`} className="absolute inset-0 h-full w-full object-contain p-2" />
                          {product.image === path && <span className="absolute left-2 top-2 bg-brand-orange px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-brand-cream" data-testid={`main-badge-${n}`}>Main</span>}
                          <span className="absolute right-2 top-2 bg-brand-charcoal/80 px-1.5 py-0.5 text-[9px] font-semibold text-brand-cream">{n + 1}</span>
                        </div>
                        <div className="flex items-center justify-between px-2 py-2">
                          <span className="flex gap-1">
                            <button onClick={() => move(n, -1)} disabled={n === 0} data-testid={`move-left-${n}`} aria-label="Move earlier" className="text-brand-stone hover:text-brand-orange disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
                            <button onClick={() => move(n, 1)} disabled={n === images.length - 1} data-testid={`move-right-${n}`} aria-label="Move later" className="text-brand-stone hover:text-brand-orange disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
                          </span>
                          {product.image !== path && <button onClick={() => setMain(path)} data-testid={`set-main-${n}`} aria-label="Set as main" className="text-brand-stone hover:text-brand-orange"><Star className="h-4 w-4" /></button>}
                          <label data-testid={`replace-${n}`} className="cursor-pointer text-[10px] font-semibold uppercase tracking-[0.1em] text-brand-stone hover:text-brand-orange">
                            Replace<input type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => { pickFile(e.target.files[0], n); e.target.value = ""; }} />
                          </label>
                          <button onClick={() => removeImage(n)} data-testid={`del-img-${n}`} aria-label="Remove photo" className="text-brand-stone hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <input ref={addInputRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => pickFile(e.target.files[0], null)} />
              </section>
            )}

            <details className="border border-brand-sand bg-brand-surface p-6">
              <summary className="cursor-pointer font-serif text-xl text-brand-charcoal" data-testid="more-details-toggle">More details (optional)</summary>
              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <div><label className={label} htmlFor="f-sku">SKU</label><input id="f-sku" data-testid="field-sku" className={input} value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="Auto if blank" /></div>
                <div><label className={label} htmlFor="f-compare">Compare price / MRP (₹)</label><input id="f-compare" data-testid="field-compare-price" type="number" min="0" className={input} value={form.compare_price} onChange={(e) => setForm({ ...form, compare_price: e.target.value })} /></div>
                <div><label className={label} htmlFor="f-cost">Cost price (₹)</label><input id="f-cost" data-testid="field-cost-price" type="number" min="0" className={input} value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} /></div>
                <div><label className={label} htmlFor="f-low">Low stock alert</label><input id="f-low" data-testid="field-low-stock" type="number" min="0" className={input} value={form.low_stock_alert} onChange={(e) => setForm({ ...form, low_stock_alert: e.target.value })} /></div>
                <div className="sm:col-span-2"><label className={label} htmlFor="f-short">Short description (used for SEO)</label><input id="f-short" data-testid="field-short-desc" className={input} maxLength={300} value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} /></div>
                <div className="sm:col-span-2"><label className={label} htmlFor="f-full">Full description</label><textarea id="f-full" data-testid="field-full-desc" rows={4} className={input} value={form.full_description} onChange={(e) => setForm({ ...form, full_description: e.target.value })} /></div>
                <div className="sm:col-span-2"><label className={label} htmlFor="f-tags">Tags (comma separated)</label><input id="f-tags" data-testid="field-tags" className={input} value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="handmade, gift, evil eye" /></div>
              </div>
            </details>
          </div>

          <div className="space-y-6">
            <section className="border border-brand-sand bg-brand-surface p-6">
              <p className="font-serif text-xl text-brand-charcoal">Status</p>
              <label className="mt-4 flex cursor-pointer items-center gap-3 text-sm text-brand-charcoal">
                <input type="checkbox" data-testid="field-featured" className="h-4 w-4 accent-brand-orange" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured on homepage
              </label>
              {!isNew && (
                <label className="mt-3 flex cursor-pointer items-center gap-3 text-sm text-brand-charcoal">
                  <input type="checkbox" data-testid="field-published" className="h-4 w-4 accent-brand-orange" checked={form.published} disabled={!form.published && !canPublish} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published (live on store)
                </label>
              )}
              {!isNew && !form.published && missing.length > 0 && (
                <p className="mt-3 text-xs text-amber-700" data-testid="publish-checklist">To publish, add: {missing.join(", ")}.</p>
              )}
              <button type="button" onClick={isNew ? onCreate : onSave} disabled={saving} data-testid={isNew ? "create-button" : "save-button"} className="mt-6 w-full bg-brand-charcoal py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-cream transition-colors hover:bg-brand-orange disabled:opacity-50">
                {saving ? "Saving…" : isNew ? "Create bracelet" : "Save changes"}
              </button>
              {isNew && <p className="mt-3 text-xs text-brand-stone">Photos (up to 4) are added on the next step.</p>}
            </section>
            {!isNew && product?.published !== false && (
              <a href={`/product/${product.slug}`} target="_blank" rel="noreferrer" data-testid="view-live" className="block border border-brand-sand bg-brand-surface p-4 text-center text-xs font-semibold uppercase tracking-[0.14em] text-brand-orange hover:border-brand-orange">View live page</a>
            )}
          </div>
        </div>

        {pending && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-charcoal/50 p-6" data-testid="image-preview">
            <div className="w-full max-w-sm bg-brand-cream p-6">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-charcoal">{pending.replaceIndex === null ? "New photo" : "Replacement photo"}</p>
              <div className="relative mt-4 aspect-square bg-white"><img src={pending.preview} alt="Preview" className="absolute inset-0 h-full w-full object-contain p-2" /></div>
              <p className="mt-3 truncate text-xs text-brand-stone">{pending.file.name}</p>
              <div className="mt-5 flex gap-3">
                <button onClick={savePending} disabled={saving} data-testid="image-save" className="flex-1 bg-brand-charcoal py-3 text-xs font-semibold uppercase tracking-[0.14em] text-brand-cream hover:bg-brand-orange disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
                <button onClick={cancelPending} data-testid="image-cancel" className="flex-1 border border-brand-sand py-3 text-xs font-semibold uppercase tracking-[0.14em] text-brand-charcoal hover:border-brand-orange">Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
